import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Close,
  DeleteOutline,
  ExpandMore,
  GroupAdd,
  PersonAdd,
  RadioButtonUnchecked,
  CheckBox,
  CheckBoxOutlineBlank,
  ContentCopy,
  Share,
} from '@mui/icons-material';
import type {
  Action,
  Check,
  PanelPlayer,
  TournamentWorkflow,
  WorkflowPanel,
  WorkflowPhase,
} from '@/api/generated/model';
import type { SportType } from '@/types';
import { useNotification } from '@/contexts/NotificationContext';
import { ResponsiveDialog } from '@/components/ResponsiveDialog';
import { ActionButton, ActionDialog, actionNeedsDialog, type RunAction } from './ActionButton';
import { copyText, KebabMenu } from './KebabMenu';

type ShareTarget = { title: string; url: string };
const ShareContext = createContext<ShareTarget | undefined>(undefined);

const ShareInvite: React.FC = () => {
  const target = useContext(ShareContext);
  const { showSuccess, showError } = useNotification();
  if (!target) return null;
  const canShare = typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await copyText(target.url);
      showSuccess('Ссылка скопирована — отправьте её участникам');
    } catch {
      showError('Не удалось скопировать, выделите ссылку вручную');
    }
  };

  const share = async () => {
    try {
      await navigator.share({ title: target.title, text: `Запись: ${target.title}`, url: target.url });
    } catch {
      /* пользователь закрыл системное меню */
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'inherit' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Ссылка для участников — по ней они откроют турнир и подадут заявку
      </Typography>
      <TextField
        value={target.url}
        fullWidth
        size="small"
        slotProps={{ htmlInput: { readOnly: true, onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select() } }}
        sx={{ mb: 1 }}
      />
      <Stack direction="row" spacing={1}>
        <Button variant="contained" color="manage" startIcon={<ContentCopy />} onClick={copy} sx={{ flex: 1, minHeight: 44 }}>
          Скопировать
        </Button>
        {canShare && (
          <Button variant="outlined" color="manage" startIcon={<Share />} onClick={share} sx={{ flex: 1, minHeight: 44 }}>
            Отправить
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

const phaseStatusLabel: Record<WorkflowPhase['status'], string> = {
  done: 'Готово',
  current: 'Сейчас',
  upcoming: 'Дальше',
  skipped: 'Пропущено',
};

const isQuickAction = (action?: Action) => Boolean(action && action.enabled && !action.confirm && !action.input?.length && !action.href);

/** Только то, что осталось сделать, одной тихой строкой; выполненные проверки не показываем. */
const ChecksSummary: React.FC<{ checks: Check[]; run: RunAction }> = ({ checks, run }) => {
  const pending = checks.filter((c) => c.status !== 'ok');
  if (pending.length === 0) return null;
  const fixes = pending.flatMap((c) => (c.fix ? [c.fix] : [])).filter((a, i, all) => all.findIndex((x) => x.id === a.id) === i);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
        Осталось: {pending.map((c) => (c.message || c.label).replace(/\.$/, '')).join(' · ')}
      </Typography>
      {fixes.map((fix) => (
        <ActionButton key={fix.id} action={fix} run={run} size="small" showHint={false} />
      ))}
    </Box>
  );
};

interface PlayerListProps {
  players: PanelPlayer[];
  run: RunAction;
  checkable?: boolean;
  searchable?: boolean;
}

const rowDivider = { '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } } as const;

const BottomSheet: React.FC<{ open: boolean; onClose: () => void; title: string; subtitle?: string; children: React.ReactNode }> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
}) => (
  <ResponsiveDialog open={open} onClose={onClose} title={title}>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </ResponsiveDialog>
);

/** Строка пришедшего игрока на этапе составов: состав напротив имени, выбор и удаление — в шторке. */
const LineupPlayerRow: React.FC<{ player: PanelPlayer; run: RunAction }> = ({ player, run }) => {
  const [sheet, setSheet] = useState<'team' | 'remove' | null>(null);
  const [busy, setBusy] = useState(false);
  const actions = player.actions ?? [];
  const assignActions = actions.filter((a) => a.id === 'assign_player');
  const unassign = actions.find((a) => a.id === 'unassign_player');
  const remove = actions.find((a) => a.id === 'uncheck');
  const teamName = player.badge;

  const exec = async (action: Action) => {
    setBusy(true);
    try {
      await run(action, action.params);
      setSheet(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 48, ...rowDivider }}>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {player.name}
      </Typography>
      {teamName ? (
        <Chip
          label={teamName}
          color="manage"
          variant="outlined"
          onClick={() => setSheet('team')}
          onDelete={() => setSheet('team')}
          deleteIcon={<ExpandMore />}
          sx={{ height: 36, fontWeight: 600, maxWidth: '45%' }}
        />
      ) : (
        <>
          {assignActions.length > 0 && (
            <Button size="small" variant="contained" color="manage" onClick={() => setSheet('team')} sx={{ minHeight: 36 }}>
              В команду
            </Button>
          )}
          {remove && (
            <IconButton aria-label={remove.label} title={remove.label} onClick={() => setSheet('remove')} sx={{ width: 40, height: 40, color: 'text.secondary' }}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          )}
        </>
      )}

      <BottomSheet
        open={sheet === 'team'}
        onClose={() => setSheet(null)}
        title={player.name}
        subtitle={teamName ? `Сейчас в составе «${teamName}»` : 'Выберите состав'}
      >
        <Stack spacing={1}>
          {assignActions.map((action) => (
            <Button
              key={JSON.stringify(action.params ?? {})}
              fullWidth
              variant="outlined"
              color="manage"
              disabled={busy || !action.enabled}
              onClick={() => void exec(action)}
              sx={{ minHeight: 48, justifyContent: 'flex-start' }}
            >
              {action.label}
            </Button>
          ))}
          {assignActions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Других составов нет — добавьте состав.
            </Typography>
          )}
          {unassign && (
            <Button fullWidth disabled={busy} onClick={() => void exec(unassign)} sx={{ minHeight: 48 }}>
              {unassign.label}
            </Button>
          )}
          {teamName && remove && (
            <Button fullWidth color="error" disabled={busy} onClick={() => setSheet('remove')} sx={{ minHeight: 48 }}>
              {remove.label}
            </Button>
          )}
        </Stack>
      </BottomSheet>

      {remove && (
        <BottomSheet
          open={sheet === 'remove'}
          onClose={() => setSheet(null)}
          title={remove.confirm?.title ?? remove.label}
          subtitle={remove.confirm?.text}
        >
          <Stack direction="row" spacing={1}>
            <Button fullWidth variant="outlined" disabled={busy} onClick={() => setSheet(null)} sx={{ minHeight: 48 }}>
              Отмена
            </Button>
            <Button fullWidth variant="contained" color="error" disabled={busy} onClick={() => void exec(remove)} sx={{ minHeight: 48 }}>
              Убрать
            </Button>
          </Stack>
        </BottomSheet>
      )}
    </Box>
  );
};

/** Список игроков панели. Одно простое действие — по тапу; несколько — в шторке. */
const PlayerList: React.FC<PlayerListProps> = ({ players, run, checkable, searchable }) => {
  const [sheet, setSheet] = useState<PanelPlayer | null>(null);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const filtered = query ? players.filter((p) => p.name.toLowerCase().includes(query.toLowerCase())) : players;

  const tap = async (player: PanelPlayer) => {
    const actions = player.actions ?? [];
    if (actions.length === 1 && isQuickAction(actions[0])) {
      setBusyId(player.id);
      try {
        await run(actions[0]);
      } finally {
        setBusyId(null);
      }
      return;
    }
    if (actions.length > 0) setSheet(player);
  };

  return (
    <>
      {searchable && players.length > 8 && (
        <TextField size="small" fullWidth label="Поиск" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ mb: 1 }} />
      )}
      <List dense disablePadding>
        {filtered.map((player) => (
          <ListItemButton
            key={player.id}
            onClick={() => void tap(player)}
            disabled={busyId === player.id || !(player.actions?.length)}
            sx={{ minHeight: 48, px: 0, ...rowDivider }}
          >
            {checkable && (
              <ListItemIcon sx={{ minWidth: 36 }}>
                {player.selected ? <CheckBox color="success" /> : <CheckBoxOutlineBlank />}
              </ListItemIcon>
            )}
            <ListItemText primary={player.name} />
            {player.badge && <Chip size="small" label={player.badge} variant="outlined" />}
          </ListItemButton>
        ))}
      </List>
      <BottomSheet open={Boolean(sheet)} onClose={() => setSheet(null)} title={sheet?.name ?? ''}>
        <Stack spacing={1}>
          {sheet?.actions?.map((action) => (
            <ActionButton
              key={`${action.id}-${JSON.stringify(action.params ?? {})}`}
              action={action}
              fullWidth
              size="medium"
              run={async (a, v) => {
                await run(a, v);
                setSheet(null);
              }}
            />
          ))}
        </Stack>
      </BottomSheet>
    </>
  );
};

const PANEL_ACTION_ICONS: Record<string, React.ReactNode> = {
  add_team: <GroupAdd />,
  add_player: <PersonAdd />,
  create_player: <Add />,
};

const PanelActions: React.FC<{ actions?: Action[]; run: RunAction; sportType?: SportType }> = ({ actions, run, sportType }) =>
  actions?.length ? (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
      {actions.map((action) => (
        <ActionButton
          key={action.id}
          action={action}
          run={run}
          size="small"
          sportType={sportType}
          showHint={false}
          startIcon={PANEL_ACTION_ICONS[action.id]}
        />
      ))}
    </Stack>
  ) : null;

const TeamPlayerRow: React.FC<{ player: PanelPlayer; run: RunAction }> = ({ player, run }) => {
  const [busy, setBusy] = useState(false);
  const unassign = player.actions?.find((a) => a.id === 'unassign_player');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 44, ...rowDivider }}>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {player.name}
      </Typography>
      {unassign && (
        <IconButton
          aria-label={unassign.label}
          title={unassign.label}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await run(unassign, unassign.params);
            } finally {
              setBusy(false);
            }
          }}
          sx={{ width: 40, height: 40, color: 'text.secondary' }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

/** Состав: шапка (название, число, ⋮) и игроки с «✕». */
const TeamCard: React.FC<{ team: NonNullable<WorkflowPanel['teams']>[number]; run: RunAction }> = ({ team, run }) => {
  const [dialogAction, setDialogAction] = useState<Action | null>(null);
  return (
    <Paper variant="outlined" sx={{ bgcolor: 'inherit', overflow: 'hidden' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, pr: 0.5, minHeight: 44, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {team.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {team.players.length}
        </Typography>
        <KebabMenu
          label={`Действия с составом ${team.name}`}
          items={(team.actions ?? []).map((action) => ({
            id: action.id,
            label: action.label,
            danger: action.style === 'danger',
            hidden: !action.enabled,
            onClick: () => (actionNeedsDialog(action) ? setDialogAction(action) : run(action, action.params)),
          }))}
        />
      </Box>
      <Box sx={{ px: 1.5 }}>
        {team.players.length ? (
          team.players.map((player) => <TeamPlayerRow key={player.id} player={player} run={run} />)
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1.25 }}>
            Пока пусто
          </Typography>
        )}
      </Box>
      <ActionDialog action={dialogAction} run={run} onClose={() => setDialogAction(null)} />
    </Paper>
  );
};

const PanelView: React.FC<{ panel: WorkflowPanel; run: RunAction; sportType?: SportType }> = ({ panel, run, sportType }) => {
  const navigate = useNavigate();

  const body = (() => {
    switch (panel.kind) {
      case 'check_in':
        return <PlayerList players={panel.players ?? []} run={run} checkable searchable />;
      case 'signups':
      case 'registrations':
        return (
          <>
            <ShareInvite />
            {panel.players?.length ? (
              <PlayerList players={panel.players} run={run} />
            ) : (
              <Typography color="text.secondary">Пока никого</Typography>
            )}
          </>
        );
      case 'lineups':
        return (
          <Stack spacing={1.5}>
            {(panel.teams ?? []).map((team) => (
              <TeamCard key={team.id} team={team} run={run} />
            ))}
            {(panel.players?.length ?? 0) > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Без состава · {panel.players?.length}
                </Typography>
                {(panel.players ?? []).map((player) => (
                  <LineupPlayerRow key={player.id} player={player} run={run} />
                ))}
              </Box>
            )}
          </Stack>
        );
      case 'next_match':
        return (
          <Stack spacing={1}>
            {panel.proposal && (
              <Box>
                <Typography variant="h4">
                  {panel.proposal.homeName} — {panel.proposal.awayName}
                </Typography>
                {panel.proposal.hint && (
                  <Typography variant="body2" color="text.secondary">
                    {panel.proposal.hint}
                  </Typography>
                )}
              </Box>
            )}
            {(panel.actions ?? []).map((action) => (
              <ActionButton key={action.id} action={action} run={run} fullWidth sportType={sportType} />
            ))}
          </Stack>
        );
      case 'schedule': {
        const matches = [...(panel.matches ?? [])].sort((a, b) => Number(b.status === 'live') - Number(a.status === 'live'));
        return matches.length ? (
          <List dense disablePadding>
            {matches.map((m) => (
              <ListItemButton key={m.id} onClick={() => m.href && navigate(m.href)} sx={{ minHeight: 48, px: 0 }}>
                <ListItemText primary={m.title} secondary={m.subtitle} />
                {m.status === 'live' && (
                  <Chip
                    label="LIVE"
                    size="small"
                    sx={{ bgcolor: '#ff4444', color: 'white', fontSize: '0.65rem', height: 20, fontWeight: 'bold', mr: m.score ? 1 : 0 }}
                  />
                )}
                {m.score && <Typography fontWeight={700}>{m.score}</Typography>}
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary">Матчей нет</Typography>
        );
      }
      case 'day_standings':
        return panel.rows?.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Состав</TableCell>
                <TableCell align="right">И</TableCell>
                <TableCell align="right">Г</TableCell>
                <TableCell align="right">О</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {panel.rows.map((r) => (
                <TableRow key={r.participantId}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{r.played ?? 0}</TableCell>
                  <TableCell align="right">
                    {r.goalsFor ?? 0}:{r.goalsAgainst ?? 0}
                  </TableCell>
                  <TableCell align="right">
                    <b>{r.points ?? 0}</b>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography color="text.secondary">Матчей ещё не было</Typography>
        );
      default:
        return null;
    }
  })();

  return (
    <Box>
      {(panel.title || panel.summary) && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
          {panel.title && <Typography variant="h5">{panel.title}</Typography>}
          {panel.summary && (
            <Typography variant="body2" color="text.secondary">
              {panel.summary}
            </Typography>
          )}
        </Box>
      )}
      {body}
      {panel.kind !== 'next_match' && <PanelActions actions={panel.actions} run={run} sportType={sportType} />}
    </Box>
  );
};

const PhaseBody: React.FC<{ phase: WorkflowPhase; run: RunAction; sportType?: SportType }> = ({ phase, run, sportType }) => (
  <Stack spacing={2}>
    <ChecksSummary checks={phase.checks} run={run} />
    {phase.panels?.map((panel, i) => (
      <React.Fragment key={`${panel.kind}-${i}`}>
        {i > 0 && <Divider />}
        <PanelView panel={panel} run={run} sportType={sportType} />
      </React.Fragment>
    ))}
    {phase.primaryAction && <ActionButton action={phase.primaryAction} run={run} fullWidth sportType={sportType} />}
    {phase.actions.length > 0 && (
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {phase.actions.map((action) => (
          <ActionButton key={action.id} action={action} run={run} size="small" sportType={sportType} />
        ))}
      </Stack>
    )}
  </Stack>
);

interface WorkflowViewProps {
  workflow: TournamentWorkflow;
  run: RunAction;
  sportType?: SportType;
  /** Публичная ссылка на турнир: показывается в панелях записи, чтобы разослать участникам. */
  share?: ShareTarget;
}

/** Рендер сценария турнира с сервера: фазы, проверки, панели и действия. */
export const WorkflowView: React.FC<WorkflowViewProps> = ({ workflow, run, sportType, share }) => {
  const currentId = workflow.phases.find((p) => p.status === 'current')?.id;
  const [expanded, setExpanded] = useState<string | undefined>(currentId);
  const [lastCurrent, setLastCurrent] = useState(currentId);
  if (currentId !== lastCurrent) {
    setLastCurrent(currentId);
    setExpanded(currentId);
  }

  return (
    <ShareContext.Provider value={share}>
    <Box sx={{ borderLeft: 3, borderColor: 'manage.main', pl: { xs: 1, sm: 2 } }}>
      {workflow.phases.map((phase) => {
        const icon =
          phase.status === 'done' ? (
            <CheckCircle color="success" fontSize="small" />
          ) : phase.status === 'current' ? (
            <RadioButtonUnchecked sx={{ color: 'manage.main' }} fontSize="small" />
          ) : (
            <RadioButtonUnchecked color="disabled" fontSize="small" />
          );
        return (
          <Accordion
            key={phase.id}
            disableGutters
            expanded={expanded === phase.id}
            onChange={(_, open) => setExpanded(open ? phase.id : undefined)}
            sx={{ bgcolor: 'inherit', opacity: phase.status === 'upcoming' || phase.status === 'skipped' ? 0.7 : 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 56 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {icon}
                <Typography fontWeight={phase.status === 'current' ? 700 : 500} sx={{ flex: 1 }}>
                  {phase.title}
                </Typography>
                <Chip
                  size="small"
                  label={phaseStatusLabel[phase.status]}
                  color={phase.status === 'current' ? 'manage' : phase.status === 'done' ? 'success' : 'default'}
                  variant={phase.status === 'current' ? 'filled' : 'outlined'}
                  sx={{ mr: 1 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <PhaseBody phase={phase} run={run} sportType={sportType} />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
    </ShareContext.Provider>
  );
};
